import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Flex, Button, Text } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { FormBuilder } from '@/components/formBuilder';
import { useEmailField } from '@/components/formBuilder/formFields/useEmailField';
import { useAuth, useShowToast } from '@/hooks';
import { useGetAllUsers } from '@/hooks/useGetAllUsers';
import { selectUsers } from '@/store/users/selectors';
import { templatesCategoryConfig } from '@/constants/templates-category';
import { useGetTemplatesVariablesQuery } from '@/store/query/email-templates-settings';
import { getInvalidVariableAlertConfig } from '@/utils/getInvalidVariableAlertConfig';
import { checkForInvalidVariables } from '@/utils/commonHelpers';
import { CustomScheduleAt } from '@/components/ats/job-stage-actions/actions/email/CustomScheduleAt';
import { sendComposedEmail } from '../../../api/email';
import type { EmailComposeBlockData } from '../../../types';
import { HTML_TAG_REGEX } from '@/constants/regex';
import {
  addEmailAttachmentPolicy,
  validateEmailAttachments,
} from './emailAttachmentPolicy';

interface EmailComposeBlockProps {
  data: EmailComposeBlockData;
  // When rendered inside an EmailComposeGroup, register a submit handle (so
  // "Send all" can trigger this composer) and report when it has sent.
  registerSend?: (key: string, fn: (() => void) | null) => void;
  onSent?: () => void;
}

// Inline email composer rendered from a kula_email_compose block. Reuses the
// app's native email composer pieces (useEmailField + FormBuilder: From
// dropdown, rich Tiptap body with attachments, cc/bcc, variable insertion,
// schedule) so it matches the look and feel of SendEmailDrawer — but inline in
// the chat, prefilled with the agent's draft, and wired to the ad-hoc contact /
// application email endpoints (not the stage-action /ats_candidate_messages
// flow SendEmailDrawer uses). Sender defaults to the current user.
export const EmailComposeBlock = ({
  data,
  registerSend,
  onSent,
}: EmailComposeBlockProps) => {
  const formRef = useRef<any>(null);
  const { currUser } = useAuth();
  const toast = useShowToast();
  const userList = useSelector(selectUsers);
  const { processFromUserPayload, feSharedEmails } = useGetAllUsers();

  const view = templatesCategoryConfig?.candidate_adhoc?.view;
  const { data: { variables = [] } = {} } = useGetTemplatesVariablesQuery({
    params: { view },
  });
  const variablesListArr = Object.entries(variables || {}).flatMap(
    ([, values = []]) => values as string[]
  );

  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const { sessionId } = useParams<{ sessionId?: string }>();
  // Sent state is server-authoritative: on a successful send the proxy marks
  // the originating tool message sent, so a reload rebuilds this block with
  // data.sent=true and shows the terminal state — across reloads AND devices,
  // no localStorage. We also flip it optimistically here for the live session.
  const [sent, setSent] = useState<boolean>(Boolean(data.sent));

  // Register this composer's submit so an EmailComposeGroup's "Send all" can
  // trigger it; unregister on unmount.
  useEffect(() => {
    if (!registerSend) return;
    const key = data.draftId ?? data.toolCallId ?? '';
    registerSend(key, () => formRef.current?.submitForm?.());
    return () => registerSend(key, null);
  }, [registerSend, data.draftId, data.toolCallId]);

  // Recipients are fixed by the block — show them as a read-only "To" line
  // (so the recipient is always visible) rather than an editable picker. Lead
  // with the candidate's NAME (who you're emailing), with the address in
  // angle brackets when known — "Joseph Martinez <joseph@x.com>". Fall back to
  // email, then a neutral label. Internal ids must never become user-facing
  // copy when a legacy payload is missing display fields.
  const toLabel = useMemo(
    () =>
      data.recipients
        .map((r) => {
          if (r.name && r.email) return `${r.name} <${r.email}>`;
          return r.name || r.email || 'Selected recipient';
        })
        .join(', '),
    [data.recipients]
  );

  const initialValues = {
    from: currUser?.id,
    fromUserObject: currUser,
    cc: [],
    bcc: [],
    subject: data.subject ?? '',
    body: data.body ?? '',
    attachments: [],
    schedule_at: null,
  };

  const emailFieldConfigs = addEmailAttachmentPolicy(
    useEmailField({
      formRef,
      disableFrom: false,
      valueKey: 'id',
      variables,
      showInvalidVariableAlert: false,
    })
  );

  const emailFormConfig = [
    {
      id: 'emailFormConfig',
      type: 'wrapper',
      fields: emailFieldConfigs,
      Wrapper: Box,
      wrapperProps: {
        border: '1px solid',
        borderColor: 'border-color',
        borderRadius: 'sm',
      },
    },
    getInvalidVariableAlertConfig({ variablesListArr }),
    { id: 'schedulePopover', type: 'custom', Field: CustomScheduleAt },
    {
      type: 'custom',
      Field: ({ formik }: any) => {
        useEffect(() => {
          setFormData(formik?.values);
        }, [formik]);
        return null;
      },
    },
  ];

  const handleFormSubmit = async (values: any) => {
    // Already sent (e.g. "Send all" hitting an already-sent draft) — no-op.
    if (sent) return;
    const { subject, body } = values ?? {};

    // Required fields — don't fire a doomed send (and a confusing server error)
    // when the basics are missing.
    const bodyText = String(body ?? '')
      .replace(HTML_TAG_REGEX, '')
      .trim();
    if (!subject?.trim() || !bodyText) {
      toast({
        title: 'Add a subject and a message before sending',
        status: 'warning',
      });
      return;
    }

    const attachmentValidation = validateEmailAttachments(values?.attachments);
    if (!attachmentValidation.valid) {
      toast({
        title: attachmentValidation.title,
        description: attachmentValidation.description,
        status: 'warning',
      });
      return;
    }

    // Block on invalid {{variables}} — mirror SendEmailDrawer.
    const subjectVars = variablesListArr.filter(
      (v) => v !== 'sender.email_signature'
    );
    if (
      checkForInvalidVariables({
        variablesListArr: subjectVars,
        content: subject,
      }) ||
      checkForInvalidVariables({ variablesListArr, content: body })
    ) {
      document
        .querySelector('#invalid-variables-alert')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Resolve sender (shared inbox vs user), mirroring SendEmailDrawer.
    let senderId: any;
    let atsSharedEmailId: any;
    if (feSharedEmails) {
      const fp =
        processFromUserPayload({
          selectedUser: values?.fromUserObject,
          payloadKey: 'sender_id',
        }) || {};
      senderId = fp.sender_id;
      atsSharedEmailId = fp.ats_shared_email_id;
    } else {
      senderId =
        userList?.find((u: any) => u.id === values?.from)?.id ?? currUser?.id;
    }

    const ccEmails = (values?.cc ?? [])
      .map((id: any) => userList?.find((u: any) => u.id === id)?.email)
      .filter(Boolean);
    const bccEmails = (values?.bcc ?? [])
      .map((id: any) => userList?.find((u: any) => u.id === id)?.email)
      .filter(Boolean);

    let scheduledAt: string | undefined;
    const s = values?.schedule_at;
    if (typeof s === 'string') scheduledAt = s;
    else if (s?.formattedTime) scheduledAt = s.formattedTime;

    // Route the send THROUGH agentforge (it runs the same bulk-email tool
    // server-side), instead of posting to Kula directly. Multipart so the
    // attachments ride along.
    const fd = new FormData();
    fd.append('entity', data.entity);
    data.recipients.forEach((r) => fd.append('recipient_ids', r.id));
    if (senderId) fd.append('sender_id', String(senderId));
    fd.append('subject', subject ?? '');
    fd.append('body', body ?? '');
    ccEmails.forEach((e: string) => fd.append('cc', e));
    bccEmails.forEach((e: string) => fd.append('bcc', e));
    if (atsSharedEmailId)
      fd.append('ats_shared_email_id', String(atsSharedEmailId));
    if (scheduledAt) fd.append('scheduled_at', scheduledAt);
    // Let the backend mark THIS composer block sent, so a reload shows the
    // terminal state (server-authoritative, cross-device).
    if (data.toolCallId) fd.append('tool_call_id', data.toolCallId);
    if (sessionId) fd.append('session_id', sessionId);
    attachmentValidation.files.forEach((file) =>
      fd.append('attachments', file)
    );

    try {
      setSending(true);
      await sendComposedEmail(fd);
      setSent(true);
      onSent?.();
      toast({
        title: scheduledAt ? 'Email scheduled' : 'Email sending in progress',
        status: 'success',
      });
    } catch (e: any) {
      toast({
        title: 'Sending email failed',
        status: 'error',
        description: e?.message ? String(e.message) : undefined,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box
      data-test-id="agentforge-email-compose-block"
      bg="base-white"
      border="1px solid"
      borderColor="primary-200"
      borderRadius="10px"
      overflow="hidden"
    >
      <Flex
        px="16px"
        py="12px"
        borderBottom="1px solid"
        borderColor="primary-100"
        bg="primary-25"
        align="center"
        justify="space-between"
      >
        <Text fontSize="sm" fontWeight="semibold" color="primary-900">
          {sent ? 'Email sent' : 'Compose email'}
        </Text>
      </Flex>

      <Box
        px="16px"
        py="14px"
        // The chat panel's CSS reset zeroes paragraph margins, so the Tiptap
        // body renders <p> paragraphs as one dense blob (greeting, body and
        // sign-off with no breathing room). Restore email-like paragraph
        // spacing INSIDE the editor only — the sent email itself renders
        // with the client's own styles.
        sx={{
          '.ProseMirror p': { margin: '0 0 0.75em' },
          '.ProseMirror p:last-child': { marginBottom: 0 },
        }}
      >
        <Flex mb="10px" gap="8px" align="baseline">
          <Text fontSize="xs" color="secondary-500" minW="22px">
            To
          </Text>
          <Text fontSize="sm" color="secondary-900" fontWeight="medium">
            {toLabel}
          </Text>
        </Flex>

        {!sent && (
          <FormBuilder
            initialValues={initialValues}
            config={emailFormConfig}
            onSubmit={handleFormSubmit}
            formRef={formRef}
          />
        )}
      </Box>

      {!sent && (
        <Flex
          px="16px"
          py="12px"
          borderTop="1px solid"
          borderColor="border-color"
          align="center"
          justify="space-between"
        >
          <Text fontSize="xs" color="secondary-500">
            {currUser?.email
              ? `Sending as ${currUser.email}`
              : 'Sending as you'}
          </Text>
          <Button
            variant="primary"
            size="sm"
            isLoading={sending}
            onClick={() => formRef.current?.submitForm?.()}
          >
            {formData?.schedule_at ? 'Schedule email' : 'Send email'}
          </Button>
        </Flex>
      )}
    </Box>
  );
};
